import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../api.service';
import {
  AdminBooking,
  BookingChannelFilter,
  BookingStatusFilter,
} from '../../pages/admin/booking-management/booking-management.models';

export interface AdminBookingListParams {
  status?: BookingStatusFilter;
  channel?: BookingChannelFilter;
  q?: string;
  per_page?: string;
  page?: string;
}

export interface AdminBookingListResponse {
  success: boolean;
  data: {
    bookings: AdminBooking[];
    meta: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
    filter_counts: {
      status: Record<string, number>;
      channel: Record<string, number>;
    };
  };
}

export interface AdminBookingMutationResponse {
  success: boolean;
  message: string;
  data: {
    booking: AdminBooking;
  };
}

export interface AdminBookingCreatePayload {
  user_id?: number;
  rooms?: { room_type_id: number; room_id?: number }[];
  room_id?: number;
  check_in: string;
  check_out: string;
  guest_count: number;
  guest_name: string;
  guest_email: string;
  guest_phone: string;
  guest_nationality?: string;
  guest_document_type?: 'passport' | 'id';
  guest_document_number?: string;
  special_request?: string;
  internal_notes?: string;
  payment_method: string;
  channel: 'front-desk';
}

export interface AdminBookingQuoteLine {
  room_type_id: number;
  name: string;
  nightly_price: number;
  nights: number;
  line_total: number;
}

export interface AdminBookingQuote {
  nights: number;
  lines: AdminBookingQuoteLine[];
  subtotal: number;
  cleaning_fee: number;
  tax_amount: number;
  deposit_amount: number;
  discount: number;
  total: number;
}

export interface AdminAvailableRoom {
  id: number;
  code: string;
  floor: string | null;
}

export interface AdminBookingDetail extends AdminBooking {
  guestId?: number | null;
  roomCategory?: string | null;
  roomSizeSqm?: number | null;
  roomBed?: string | null;
  roomMaxGuests?: number | null;
  roomImage?: string;
  roomTypeId?: number | null;
  roomCode?: string | null;
  checkInTime?: string;
  checkOutTime?: string;
  check_in?: string;
  check_out?: string;
  paymentMethod?: string;
  createdAt?: string | null;
  subtotal?: number;
  cleaningFee?: number;
  taxAmount?: number;
  depositAmount?: number;
  discount?: number;
  specialRequest?: string | null;
  internalNotes?: string | null;
  guest?: {
    id?: number;
    name?: string;
    email?: string;
    phone?: string | null;
    nationality?: string | null;
    documentType?: string | null;
    documentNumber?: string | null;
    initials?: string;
  };
  pricingLines?: {
    name: string;
    nights: number;
    nightlyPrice: number;
    lineTotal: number;
    roomCode?: string | null;
  }[];
  bookingRooms?: AdminBookingRoomDetail[];
  timeline?: { label: string; at: string | null; tone: string }[];
}

export interface AdminBookingRoomDetail {
  roomTypeId: number;
  roomTypeName: string;
  roomCategory?: string | null;
  roomSizeSqm?: number | null;
  roomBed?: string | null;
  roomMaxGuests?: number | null;
  roomImage?: string;
  roomId?: number | null;
  roomCode?: string | null;
  roomFloor?: string | null;
  nightlyPrice?: number;
}

export interface AdminBookingDetailResponse {
  success: boolean;
  data: {
    booking: AdminBookingDetail;
  };
}

export interface AdminBookingQuoteResponse {
  success: boolean;
  data: {
    quote: AdminBookingQuote;
  };
}

export interface AdminAvailableRoomsResponse {
  success: boolean;
  data: {
    rooms: AdminAvailableRoom[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class AdminBookingService {
  private readonly basePath = '/admin/bookings';

  constructor(private readonly api: ApiService) {}

  list(params?: AdminBookingListParams): Observable<AdminBookingListResponse> {
    const query: Record<string, string> = {};

    if (params?.status && params.status !== 'all') {
      query['status'] = params.status;
    }

    if (params?.channel && params.channel !== 'all') {
      query['channel'] = params.channel;
    }

    if (params?.q) {
      query['q'] = params.q;
    }

    if (params?.per_page) {
      query['per_page'] = params.per_page;
    }

    if (params?.page) {
      query['page'] = params.page;
    }

    return this.api.get<AdminBookingListResponse>(
      this.basePath,
      Object.keys(query).length > 0 ? query : undefined,
    );
  }

  get(id: string): Observable<AdminBookingDetailResponse> {
    return this.api.get<AdminBookingDetailResponse>(`${this.basePath}/${id}`);
  }

  quote(params: {
    check_in: string;
    check_out: string;
    room_ids: number[];
    discount_amount?: number;
  }): Observable<AdminBookingQuoteResponse> {
    const query: Record<string, string> = {
      check_in: params.check_in,
      check_out: params.check_out,
    };

    params.room_ids.forEach((roomId, index) => {
      query[`room_ids[${index}]`] = String(roomId);
    });

    if (params.discount_amount !== undefined) {
      query['discount_amount'] = String(params.discount_amount);
    }

    return this.api.get<AdminBookingQuoteResponse>(`${this.basePath}/quote`, query);
  }

  availableRooms(params: {
    room_type_id: number;
    check_in: string;
    check_out: string;
    exclude_booking_id?: number;
  }): Observable<AdminAvailableRoomsResponse> {
    const query: Record<string, string> = {
      room_type_id: String(params.room_type_id),
      check_in: params.check_in,
      check_out: params.check_out,
    };

    if (params.exclude_booking_id !== undefined) {
      query['exclude_booking_id'] = String(params.exclude_booking_id);
    }

    return this.api.get<AdminAvailableRoomsResponse>(
      `${this.basePath}/available-rooms`,
      query,
    );
  }

  create(payload: AdminBookingCreatePayload): Observable<AdminBookingMutationResponse> {
    return this.api.post<AdminBookingMutationResponse>(this.basePath, payload);
  }

  updateInternalNotes(id: string, internal_notes: string): Observable<AdminBookingMutationResponse> {
    return this.api.put<AdminBookingMutationResponse>(`${this.basePath}/${id}`, { internal_notes });
  }

  updateStatus(id: string, status: string): Observable<AdminBookingMutationResponse> {
    return this.api.patch<AdminBookingMutationResponse>(`${this.basePath}/${id}/status`, { status });
  }
}
